import _ from 'lodash';
import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';

import { Card } from 'semantic-ui-react';
import {
  ErrorHandler,
  usePaginatedQuery,
  InfiniteScrollComponent,
} from '@playbookux/playbook-web-core';

import { toast } from 'react-semantic-toasts';
import qs from 'qs';
import Tabs from '../../constants/tabs';
import { dashboardProjectData } from '../../demo-project';

import {
  Container,
  CreateNewProject,
  CreateProject,
  Dropdown,
  FilterContainer,
  Main,
  Button,
  PlusSign,
  Search,
  FolderDropdown,
  ContainerFolderDropdown,
  ContainerDropdownStatus,
  ContainerSearch,
  ContainerButtonCreate,
  ContainerOptions,
  ContainerUserDropdown,
  UserDropdown,
} from './styles';

import { saveProjectType, resetProject } from '../../../core/actions';
import { updateCompanyMutation } from './components/project/graphql';
import {
  getProjects,
  getCompanyById,
} from '../../../core/services/graphql-requests';
import withLayout from '../../../core/components/with-layout';

import ShareProjectDialog from './components/share-project-dialog';
import withGa from '../../../core/components/with-ga';
import ProjectsList from './components/projects-list';
import { ORDER, TOAST_TIME, statusOptions } from './constants';
import Banners from './components/banners';
import { registerFunctions } from '../../../core/hooks/use-function-handler';

const Dashboard = (props) => {
  const {
    onResetProject,
    history,
    companyId,
    profile,
  } = props;

  const [chars, setChars] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [status, setStatus] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [visibility, setVisibility] = useState(false);
  const [company, setCompany] = useState({});
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedAllUsers, setSelectedAllUsers] = useState(true);
  const [loadingProject, setLoadingProject] = useState(false);
  // TODO: ShareProjectDialog
  // because of fetchPolicy to no-cache, the modal is working but the pagination is not
  // if changed to network-only, the pagination works but the modal not.
  // to fix I moved the ShareProjectDialog to the parent to prevent reset state
  // maybe there is a better way?
  const [showModalShareProject, setShowModalShareProject] = useState(false);
  const [projectModal, setProjectModal] = useState(null);

  const { projectSubmittedForApproval } = qs.parse(history.location.search, { ignoreQueryPrefix: true });

  useEffect(() => {
    const { pathname, state } = history.location;
    const { uploadProjectToast, ...locationState } = state || {};

    if (uploadProjectToast) {
      toast({
        description: uploadProjectToast,
        time: TOAST_TIME,
      });

      history.replace(pathname, locationState);
    }

    if (projectSubmittedForApproval) {
      toast({
        description: 'An email has been sent to the project approver.',
        time: TOAST_TIME,
      });

      history.replace(pathname);
    }
  }, [history, projectSubmittedForApproval]);

  const fetchDemoProjectStatus = useCallback(async () => {
    if (!companyId) return;

    const companyResponse = await getCompanyById(companyId);

    setIsTyping(false);
    setIsFiltering(false);
    setVisibility(companyResponse ? !!companyResponse.company.demoProjectVisibility : false);
    setCompany(companyResponse ? companyResponse?.company : {});
  }, [companyId]);

  useEffect(() => {
    fetchDemoProjectStatus();
  }, [companyId, fetchDemoProjectStatus]);

  const handleFolders = (value) => {
    setSelectedFolders(value);
  };

  const handleUsers = (value, selectedAll) => {
    setSelectedUsers(value);
    setSelectedAllUsers(selectedAll);
  };

  const handleUpdateCompany = async () => {
    await updateCompanyMutation(companyId, {
      demoProjectVisibility: false,
    });

    fetchDemoProjectStatus();
  };

  const handleCreateProject = () => {
    onResetProject();
    history.push('create-project-v2/project-set-up');
  };

  const handlingLoadingProject = (isLoading) => {
    setLoadingProject(isLoading);
  };

  const attributes = {
    variables: {
      limit: 16,
      offset: 0,
      companyId,
      name: chars,
      description: chars,
      status: status ? _.last(status) : status,
      folderIds: selectedFolders,
      projectCreatorsIds: !selectedAllUsers ? selectedUsers : undefined,
      order: ORDER,
    },
    fetchPolicy: 'no-cache',
  };

  const {
    data = {},
    error,
    fetchMore,
    refetch,
    hasMore,
    loading,
  } = usePaginatedQuery(getProjects, attributes, { dataField: 'projects' });

  const { projects = [] } = data;

  const getInputFromSearchInput = (event) => {
    const input = event.target.value;
    const formated = _.trim(input);

    if (!_.isEmpty(formated)) {
      setChars(input);
      setIsTyping(true);
    } else {
      setChars('');
      setIsTyping(false);
    }
  };

  const setStatusWithValueSelectedOrDeleted = ({ target, currentTarget }) => {
    const { textContent, className, parentNode } = currentTarget;
    const { innerText: deleted } = parentNode;

    const input = target.innerText;
    let findStatus = [];

    if (input) {
      findStatus = _.find(statusOptions, { text: input }).value;
    }

    if (status) {
      setStatus(_.concat(status, findStatus));
    } else {
      setStatus([findStatus]);
      setIsFiltering(true);
    }

    let statusEmpty = false;

    if (_.isEmpty(textContent)) {
      if (className.includes('delete icon')) {
        const { value } = _.find(statusOptions, { text: deleted });
        const updated = _.pull(status, value);
        statusEmpty = _.isEmpty(updated);
        setStatus(statusEmpty ? null : updated);
      }

      if (statusEmpty) {
        setIsFiltering(false);
      }

      if (className.includes('clear')) {
        setStatus(null);
        setIsFiltering(false);
      }
    }
  };

  const [loadingRefetchProjects, setLoadingRefetchProjects] = useState(false);

  const refetchProjects = useCallback(async () => {
    /**
     * We check loading to ensure that the getProjects query isn't already in progress at the moment of the refetch
     * to prevent cache issues.
     * https://playbookux.atlassian.net/browse/PLBUX-6201
     */
    if (loading) return;
    setLoadingRefetchProjects(true);
    await refetch.call();
    setLoadingRefetchProjects(false);
  }, [refetch]);

  const handleRefetchProjectsRequest = useCallback(async () => {
    const projectId = history?.location?.state?.projectId;
    const projectsId = _.map(projects, (project) => project.id);
    const alreadyFetched = _.includes(projectsId, projectId);
    if (projectId && projectsId.length > 0 && !alreadyFetched && loadingRefetchProjects === false) {
      await refetchProjects();
    }
  }, [history, loadingRefetchProjects, projects, refetchProjects]);

  useEffect(() => {
    handleRefetchProjectsRequest();
  }, [history, refetch, data, handleRefetchProjectsRequest]);

  const projectsList = useMemo(() => {
    if (visibility) {
      if (
        (!isFiltering && !isTyping)
        || (isFiltering && !isTyping && status === 'done')
      ) {
        return _.concat(projects, dashboardProjectData);
      }
    }
    return projects;
  }, [isFiltering, isTyping, projects, status, visibility]);

  if (!companyId) {
    return <Redirect to="/create-workspace" />;
  }

  if (error) {
    return (
      <ErrorHandler
        error={error}
        defaultMessage="Error while retrieving projects."
      />
    );
  }

  const handleUpdateCompanyProjects = () => {
    refetchProjects();
  };

  registerFunctions('dashboard_projects', {
    func1: handleUpdateCompanyProjects,
  });

  return (
    <Container>
      <Main>
        <ContainerOptions>
          <Banners
            companyId={companyId}
            userId={profile.user_id}
          />
          <FilterContainer>
            <ContainerSearch>
              <Search
                placeholder="Search project title, description"
                input={{
                  icon: 'search',
                  iconPosition: 'left',
                }}
                loading={false}
                onSearchChange={(event) => getInputFromSearchInput(event)}
                open={false}
                value={chars}
              />
            </ContainerSearch>
            <ContainerDropdownStatus>
              <Dropdown
                clearable
                multiple
                selection
                onChange={setStatusWithValueSelectedOrDeleted}
                options={statusOptions}
                placeholder="Status"
              />
            </ContainerDropdownStatus>

            <ContainerFolderDropdown>
              <FolderDropdown
                clearable
                multiple
                selection
                onChange={handleFolders}
                companyId={companyId}
                value={selectedFolders}
              />
            </ContainerFolderDropdown>

            <ContainerUserDropdown>
              <UserDropdown
                clearable
                multiple
                selection
                onChange={handleUsers}
                companyId={companyId}
                value={selectedUsers}
                userId={profile.user_id}
              />
            </ContainerUserDropdown>

            <ContainerButtonCreate>
              <Button
                onClick={handleCreateProject}
                loading={loading}
                disabled={loading}
                data-dashboard-project-creation
              >
                Create Project
              </Button>
            </ContainerButtonCreate>
          </FilterContainer>
        </ContainerOptions>

        <Card.Group itemsPerRow={4}>
          <InfiniteScrollComponent
            hasMore={hasMore}
            onLoadMore={fetchMore}
            useWindow
          >
            <ProjectsList
              status={status}
              loading={loading}
              refetch={refetch}
              handlingLoadingProject={handlingLoadingProject}
              handleUpdateCompany={handleUpdateCompany}
              attributes={attributes}
              company={company}
              projects={projects}
              setProjectModal={setProjectModal}
              showModalShareProject={showModalShareProject}
              setShowModalShareProject={setShowModalShareProject}
              isTyping={isTyping}
              isFiltering={isFiltering}
              visibility={visibility}
              loadingProject={loadingProject}
              projectsList={projectsList}
            />
            <CreateProject onClick={handleCreateProject}>
              <PlusSign>+</PlusSign>
              <CreateNewProject>Create New Project</CreateNewProject>
            </CreateProject>
          </InfiniteScrollComponent>

        </Card.Group>
      </Main>

      <ShareProjectDialog
        show={showModalShareProject}
        handleClose={() => {
          setShowModalShareProject(false);
          setProjectModal(null);
        }}
        project={projectModal}
      />

    </Container>
  );
};

Dashboard.defaultProps = {
  companyId: null,
  company: {},
};

Dashboard.propTypes = {
  history: PropTypes.shape().isRequired,
  companyId: PropTypes.string,
  profile: PropTypes.shape({
    user_id: PropTypes.string,
  }).isRequired,
  onResetProject: PropTypes.func.isRequired,
  company: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    plan: PropTypes.string,
    stripeCustomerId: PropTypes.string,
  }),
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
  profile: authentication.profile,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    onSaveProjectType: saveProjectType,
    onResetProject: resetProject,
  },
  dispatch,
);

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withLayout({ selectedTab: Tabs.PROJECT }),
  withGa,
)(Dashboard);
